// client/src/modals/labEngine/WirelessAddressModal.jsx

import React, { useState, useEffect } from 'react';
import CustomButton from '../../components/customs/customButton';

const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
const ALPHANUM_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

function validateAddress(wirelessType, value) {
  if (!value) return null; // empty allowed — student hasn't set it yet
  if (wirelessType === 'WiFi') {
    const match = value.match(IPV4_PATTERN);
    if (!match) return 'Format must be nnn.nnn.nnn.nnn';
    const outOfRange = match.slice(1).some(octet => Number(octet) > 255);
    if (outOfRange) return 'Each segment must be 0–255';
    return null;
  }
  // LoRa — generic alphanumeric node id
  if (!ALPHANUM_PATTERN.test(value)) {
    return 'Use 1–32 chars: letters, numbers, _ or -';
  }
  return null;
}

const WirelessAddressModal = ({
  visible,
  wirelessType,           // 'WiFi' | 'LoRa'
  nodeAddress,
  peerAddress,
  peerStatus,             // result of findHttpPeer / findLoRaPeer, or null
  onClose,
  onSave,                 // (nodeAddress, peerAddress) => Promise
  onCheckPeer,            // (peerAddress) => Promise — re-runs the peer lookup
}) => {
  const [localNode, setLocalNode] = useState(nodeAddress || '');
  const [localPeer, setLocalPeer] = useState(peerAddress || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (visible) {
      setLocalNode(nodeAddress || '');
      setLocalPeer(peerAddress || '');
      setSaveError(null);
    }
  }, [visible, nodeAddress, peerAddress]);

  if (!visible) return null;

  const isWifi = wirelessType === 'WiFi';
  const nodeLabel = isWifi ? 'IP Address' : 'Node Address';
  const peerLabel = isWifi ? 'Peer IP Address' : 'Peer Address';
  const placeholder = isWifi ? 'nnn.nnn.nnn.nnn' : 'e.g. node-A';

  const nodeError = validateAddress(wirelessType, localNode);
  const peerError = validateAddress(wirelessType, localPeer);

  const handleSave = async () => {
    if (nodeError || peerError) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(localNode, localPeer);
      onClose();
    } catch (err) {
      setSaveError(err.message || 'Failed to save address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="validation-panel-overlay">
      <div className="validation-panel-content" style={{ maxWidth: '440px' }}>
        <div className="validation-panel-header">
          <h3>{isWifi ? '📶 WiFi / HTTP Peer Address' : '📡 LoRa Address'}</h3>
          <button className="validation-close-btn" onClick={onClose}>✖</button>
        </div>

        <div className="validation-panel-body" style={{ padding: '16px' }}>
          <div style={{ marginBottom: '14px' }}>
            <label className="config-label">{nodeLabel}</label>
            <input
              type="text"
              className="property-input"
              value={localNode}
              onChange={(e) => setLocalNode(e.target.value)}
              placeholder={placeholder}
            />
            {nodeError && (
              <small style={{ color: '#ef4444', display: 'block', marginTop: '4px' }}>
                {nodeError}
              </small>
            )}
            <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
              This device's own {isWifi ? 'IP' : 'node'} address — your lab partner must use this as their Peer {nodeLabel.replace('IP ', 'IP ')}.
            </small>
          </div>

          <div style={{ marginBottom: '14px' }}>
            <label className="config-label">{peerLabel}</label>
            <input
              type="text"
              className="property-input"
              value={localPeer}
              onChange={(e) => setLocalPeer(e.target.value)}
              placeholder={placeholder}
            />
            {peerError && (
              <small style={{ color: '#ef4444', display: 'block', marginTop: '4px' }}>
                {peerError}
              </small>
            )}
            <small style={{ color: '#6b7280', display: 'block', marginTop: '4px' }}>
              Must match your lab partner's {nodeLabel}.
            </small>
          </div>

          {peerStatus && (
            <div
              style={{
                padding: '10px',
                borderRadius: '6px',
                fontSize: '12px',
                marginBottom: '10px',
                background: peerStatus.found ? '#ecfdf5' : '#fffbeb',
                border: `1px solid ${peerStatus.found ? '#10b981' : '#f59e0b'}`,
              }}
            >
              {peerStatus.found
                ? `✅ Peer found${peerStatus.boardModel ? ` (${peerStatus.boardModel})` : ''} — ${peerStatus.isRunning ? 'running' : 'idle'}`
                : '⚠️ Peer not found — confirm both Canvas Builder configs use matching addresses'}
            </div>
          )}

          {saveError && (
            <div style={{ color: '#ef4444', fontSize: '12px', marginBottom: '10px' }}>
              ❌ {saveError}
            </div>
          )}
        </div>

        <div className="validation-actions" style={{ display: 'flex', gap: '8px', padding: '0 16px 16px' }}>
          {onCheckPeer && (
            <CustomButton
              buttonTitle="Check Peer"
              action={() => onCheckPeer(localPeer)}
              bgColor="#6366f1"
              disabled={!localPeer || !!peerError}
            />
          )}
          <CustomButton
            buttonTitle={isSaving ? 'Saving...' : 'Save'}
            action={handleSave}
            bgColor="#10b981"
            disabled={isSaving || !!nodeError || !!peerError}
          />
          <CustomButton buttonTitle="Cancel" action={onClose} bgColor="#6b7280" />
        </div>
      </div>
    </div>
  );
};

export default WirelessAddressModal;